import { Dialog } from "@mui/material";
import React, { useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { t } from "../../translations";

// Ionic imports
import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
  IonModal,
  IonSpinner,
  IonTextarea,
  IonTitle,
  IonToolbar
} from "@ionic/react";
import { closeOutline } from "ionicons/icons";

import AddressDialogForm from "./address-dialog-form";
import { openNotificationWithIcon } from "./notification-bar";
import SafeGooglePlacesAutocomplete from "./SafeGooglePlacesAutocomplete";

import { POST_USER_DATA } from "../../store/action";
import { hapticPatterns } from "../../utils/haptics";
import { createLogger } from "../../utils/logger";
import { shouldUseIonicComponents } from "../../utils/platformUtils";
import { getRefreshToken, isUserAuthenticated } from "../../utils/tokenUtils";
import { UPDATE } from "../constants/request-type";
import { service_api } from "../constants/url";
import { MakeRequestAsync } from "../functions/axios";
import { decryptSingleData } from "../functions/cryptojs";

const logger = createLogger("AddressModal");

const AddressModal = ({ isOpen, onClose, onAddressAdded }) => {
  const dispatch = useDispatch();
  const userData = useSelector((state) => state.user);
  const lang = useSelector((state) => state.lang);
  const useMobileUI = shouldUseIonicComponents();

  const [address, setAddress] = useState({
    receiver_name: "",
    phone_number: "",
    street_name: "",
    line_address: "",
    instruction: "",
    lat: null,
    lng: null
  });

  const [loading, setLoading] = useState(false);

  const post_user_data = (data) => {
    dispatch({ type: POST_USER_DATA, data });
  };

  const resetAddressForm = () => {
    setAddress({
      receiver_name: "",
      phone_number: "",
      street_name: "",
      line_address: "",
      instruction: "",
      lat: null,
      lng: null
    });
  };

  const handleConvert = useCallback(async (text) => {
    try {
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(
          text
        )}&key=${import.meta.env.VITE_API_KEY_MAP}`
      );
      const data = await response.json();

      if (data.results && data.results.length > 0) {
        const result = data.results[0];
        
        if (!result.geometry || !result.geometry.location) {
          logger.error("Geocode result missing geometry or location");
          setAddress((prev) => ({ ...prev, lat: null, lng: null }));
          return;
        }
        
        const { lat, lng } = result.geometry.location;
        setAddress((prev) => ({ ...prev, street_name: text, lat, lng }));
        logger.log("Geocoding successful");
      } else {
        setAddress((prev) => ({ ...prev, lat: null, lng: null }));
        logger.log("Geocoding failed - no results");
      }
    } catch (error) {
      logger.error("Geocoding error:", error);
      setAddress((prev) => ({ ...prev, lat: null, lng: null }));
      logger.log("Geocoding failed - network error");
    }
  }, []);

  const addAddress = async () => {
    // Haptic feedback on mobile
    if (useMobileUI) {
      await hapticPatterns.buttonClick();
    }
    
    // Validation
    if (
      address?.receiver_name.length === 0 ||
      address?.phone_number.length === 0 ||
      address?.street_name.length === 0 ||
      address?.line_address.length === 0 ||
      address?.instruction.length === 0
    ) {
      return openNotificationWithIcon(
        "error",
        t("validation.fill_up_all_fie_1", lang)
      );
    }

    if (address?.lat === null || address?.lng === null) {
      return openNotificationWithIcon(
        "error",
        t("errors.address_position_error", lang)
      );
    }

    // Debug: Check user data and authentication status
    logger.log("🏠 Add Address Debug:", {
      hasUserData: !!userData,
      userId: userData?._id,
      hasToken: !!userData?.refresh_token,
      isAuthenticated: isUserAuthenticated(userData),
      addressData: address,
      isGuest: !userData?._id
    });

    setLoading(true);
    
    // Declare variables outside try block for error handling access
    let currentRefreshToken;
    let decryptedData;

    try {
      // Get refresh token WITHOUT passing post_user_data to avoid unnecessary Redux updates
      currentRefreshToken = await getRefreshToken(userData, null);

      let isAuthenticatedUser = userData?._id && currentRefreshToken;

      if (isAuthenticatedUser) {
        // Authenticated user - save to server
        const request_details = {
          type: UPDATE,
          url: service_api,
          route: `user/update-user/${userData._id}`,
          data: {
            address: [...(userData?.address || []), address]
          },
          token: currentRefreshToken
        };

        const response = await MakeRequestAsync(request_details);
        decryptedData = decryptSingleData(response.data.user);
        
        // Update Redux state - this is isolated to AddressModal component
        post_user_data({
          ...decryptedData
        });

        logger.log("✅ Address saved to server successfully");
      } else {
        // Guest user - save to local state only
        const updatedAddresses = [...(userData?.address || []), address];
        
        // Update local state
        decryptedData = {
          ...userData,
          address: updatedAddresses
        };
        
        post_user_data(decryptedData);

        logger.log("✅ Address saved to local state for guest user");
      }

      // Success feedback and cleanup (same for both paths)
      openNotificationWithIcon("success", t("success.address_added", lang));
      
      // Close modal and reset form
      resetAddressForm();
      onClose();
      
      // Notify parent component via callback
      if (onAddressAdded) {
        onAddressAdded(decryptedData);
      }

    } catch (err) {
      logger.error("❌ Add Address Error:", {
        error: err,
        message: err?.response?.data?.message,
        status: err?.response?.status,
        isJWTError: err?.response?.data?.message?.includes('jwt') || err?.response?.data?.message?.includes('token'),
        isAuthenticatedUser: !!(userData?._id && currentRefreshToken)
      });

      // Handle specific JWT/authentication errors
      if (err?.response?.data?.message?.includes('jwt') || 
          err?.response?.data?.message?.includes('token') ||
          err?.response?.status === 401) {
        return openNotificationWithIcon(
          "error",
          "Your session has expired. Please refresh the page and login again."
        );
      }

      // Handle API errors vs local state errors
      const errorMessage = err?.response?.data?.message 
        ? err.response.data.message 
        : userData?._id 
          ? t("errors.profile_update_error", lang)
          : "Failed to add address. Please try again.";

      return openNotificationWithIcon("error", errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    resetAddressForm();
    onClose();
  };

  // Mobile UI version
  if (useMobileUI) {
    return (
      <IonModal
        isOpen={isOpen}
        onDidDismiss={handleClose}
        breakpoints={[0, 0.85, 1]}
        initialBreakpoint={0.85}
        style={{ '--width': '85%', '--max-width': '85%', '--height': '85%', '--border-radius': '16px' }}
      >
        <IonHeader>
          <IonToolbar>
            <IonTitle>{t("buttons.add_an_address_2", lang)}</IonTitle>
            <IonButtons slot="end">
              <IonButton onClick={handleClose}>
                <IonIcon icon={closeOutline} />
              </IonButton>
            </IonButtons>
          </IonToolbar>
        </IonHeader>
        <IonContent className="ion-padding">
          <p style={{ marginBottom: 20, color: '#666' }}>
            {t("buttons.add_a_delivery__2", lang)}
          </p>

          <IonList>
            <IonItem>
              <IonLabel position="stacked">
                {t("common.enter_receiver_name", lang)}
              </IonLabel>
              <IonInput
                value={address.receiver_name}
                onIonChange={(e) =>
                  setAddress((prev) => ({
                    ...prev,
                    receiver_name: e.detail.value ?? ""
                  }))
                }
                clearOnEdit={false}
                type="text"
              />
            </IonItem>

            <IonItem>
              <IonLabel position="stacked">
                {t("common.enter_phone_number", lang)}
              </IonLabel>
              <IonInput
                value={address.phone_number}
                onIonChange={(e) =>
                  setAddress((prev) => ({
                    ...prev,
                    phone_number: e.detail.value ?? ""
                  }))
                }
                clearOnEdit={false}
                type="tel"
              />
            </IonItem>

            <IonItem lines="none" style={{ '--padding-start': '0', overflow: 'visible' }}>
              <div style={{ width: '100%' }}>
                <IonLabel position="stacked" style={{ marginBottom: '4px' }}>
                  {t("common.enter_your_street_name", lang)}
                </IonLabel>
                <SafeGooglePlacesAutocomplete
                  apiKey={import.meta.env.VITE_API_KEY_MAP}
                  selectProps={{
                    onChange: (e) => {
                      handleConvert(e?.label);
                    },
                    styles: {
                      control: () => ({
                        marginTop: 4
                      })
                    },
                    placeholder: t("common.enter_your_street_name", lang)
                  }}
                  apiOptions={{ language: "fr" }}
                  autocompletionRequest={{}}
                />
              </div>
            </IonItem>

            <IonItem>
              <IonLabel position="stacked">
                {t("common.enter_your_line_address", lang)}
              </IonLabel>
              <IonInput
                value={address.line_address}
                onIonChange={(e) =>
                  setAddress((prev) => ({
                    ...prev,
                    line_address: e.detail.value ?? ""
                  }))
                }
                clearOnEdit={false}
                type="text"
              />
            </IonItem>

            <IonItem>
              <IonLabel position="stacked">
                {t("common.enter_delivery_instruction", lang)}
              </IonLabel>
              <IonTextarea
                value={address.instruction}
                onIonChange={(e) =>
                  setAddress((prev) => ({
                    ...prev,
                    instruction: e.detail.value ?? ""
                  }))
                }
                clearOnEdit={false}
                rows={3}
              />
            </IonItem>
          </IonList>

          <IonButton
            expand="block"
            color="danger"
            onClick={addAddress}
            disabled={loading}
            style={{ marginTop: 20 }}
          >
            {loading ? (
              <IonSpinner name="crescent" />
            ) : (
              t("section_cart.add", lang)
            )}
          </IonButton>
        </IonContent>
      </IonModal>
    );
  }

  // Web UI version (unchanged)
  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
    >
      <AddressDialogForm
        titleKey="buttons.add_an_address_2"
        descriptionKey="buttons.add_a_delivery__2"
        buttonTextKey="section_cart.add"
        address={address}
        setAddress={setAddress}
        handleConvert={handleConvert}
        onSubmit={addAddress}
        loading={loading}
        lang={lang}
        containerStyle={{ minWidth: 300, height: "100%" }}
      />
    </Dialog>
  );
};

export default AddressModal;